import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import type { RoleSummary } from '@skewb-works/contracts';
import {
  AlertCircle,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Can } from '../components/Can';
import { useDeleteRole, useRoles } from '../hooks/useAccessControlApi';

export function RoleManagementPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const { data, isLoading, isError } = useRoles();
  const deleteRole = useDeleteRole();

  const roles = useMemo<RoleSummary[]>(() => data?.data ?? [], [data]);

  const filteredRoles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return roles;
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(term) ||
        (role.description?.toLowerCase().includes(term) ?? false),
    );
  }, [roles, search]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Role Management"
        subtitle="Manage access-control roles and their permissions"
        icon={<ShieldCheck className="w-7 h-7 text-primary" />}
        actions={
          <Can action="create" subject="AccessControl">
            <Button onClick={() => navigate('/settings/role-management/new')}>
              <Plus className="w-4 h-4 mr-1.5" />
              New role
            </Button>
          </Can>
        }
      />

      <div className="flex-1 overflow-y-auto px-1">
        <div className="pt-2 pb-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search roles..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10 w-[260px]"
              />
            </div>
            {search !== '' && (
              <Button
                variant="ghost"
                onClick={() => setSearch('')}
                className="h-10 gap-1.5 text-muted-foreground"
              >
                <X className="w-4 h-4" />
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="pb-6">
          <div className="border rounded-lg overflow-hidden">
            <Table className="[&_th]:h-11 [&_td]:py-2">
              <TableHeader className="sticky top-0 bg-card/90 backdrop-blur-sm z-10">
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-foreground font-semibold">Name</TableHead>
                  <TableHead className="text-foreground font-semibold">Description</TableHead>
                  <TableHead className="text-foreground font-semibold">Full access</TableHead>
                  <TableHead className="text-foreground font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRoles.map((role) => (
                  <TableRow key={role.id} className="border-border hover:bg-accent/50">
                    <TableCell className="font-semibold">
                      <span className="flex items-center gap-2">
                        {role.name}
                        {role.isSystem && (
                          <span
                            className="flex items-center gap-1 text-xs text-muted-foreground"
                            title="System role — cannot be deleted"
                          >
                            <Lock className="w-3 h-3" />
                            System
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {role.description ?? '—'}
                    </TableCell>
                    <TableCell>
                      {role.isAdmin ? (
                        <Badge variant="warning" className="gap-1 text-nowrap">
                          <ShieldCheck className="size-3 shrink-0" />
                          Full access
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Can action="update" subject="AccessControl">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="size-8 p-0"
                            aria-label="Edit role"
                            onClick={() => navigate(`/settings/role-management/${role.id}`)}
                          >
                            <Pencil className="size-4" />
                          </Button>
                        </Can>
                        <Can action="delete" subject="AccessControl">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="size-8 p-0"
                            aria-label="Delete role"
                            disabled={role.isSystem}
                            onClick={() => setDeleteConfirmId(role.id)}
                          >
                            <Trash2 className="size-4 text-destructive" />
                          </Button>
                        </Can>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {isLoading && (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
                  <p>Loading roles...</p>
                </div>
              </div>
            )}
            {isError && !isLoading && (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400 opacity-70" />
                  <p>Failed to load roles</p>
                </div>
              </div>
            )}
            {!isLoading && !isError && filteredRoles.length === 0 && (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                <div className="text-center">
                  <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No roles found</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete role</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this role? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteRole.isPending}
              onClick={() => {
                if (deleteConfirmId !== null) {
                  deleteRole.mutate(deleteConfirmId, { onSuccess: () => setDeleteConfirmId(null) });
                }
              }}
            >
              {deleteRole.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
